/**
 * Smart Docs Store
 * State management for entity-aware documents
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { JSONContent } from '@tiptap/react'
import type {
  SmartDocument,
  DetectedEntity,
  EntityStatus,
  DetectionState,
} from '@/types/document'
import { analyzeContent } from '@/lib/interviewer/analyzer'
import type { Facet } from '@/types/interviewer'

// Generate unique IDs
const generateId = () => `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
const generateEntityId = () => `ent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Default visible entity types
const ALL_ENTITY_TYPES = ['Problem', 'Person', 'Decision', 'Risk', 'Opportunity', 'ActionItem', 'Constraint']

interface DocumentStore {
  // State
  documents: SmartDocument[]
  currentDocumentId: string | null
  detectionState: DetectionState
  visibleEntityTypes: Set<string>

  // Document Actions
  createDocument: (title?: string) => SmartDocument
  createDocumentFromTemplate: (
    title: string,
    content: JSONContent,
    plainText: string,
    templateId?: string
  ) => SmartDocument
  updateDocument: (id: string, updates: Partial<SmartDocument>) => void
  deleteDocument: (id: string) => void
  setCurrentDocument: (id: string | null) => void

  // Content Actions
  updateContent: (id: string, content: JSONContent, plainText: string) => void
  setContent: (id: string, content: JSONContent, plainText: string) => void

  // Entity Detection Actions
  analyzeDocument: (id: string) => Promise<void>
  addDetectedEntities: (docId: string, entities: DetectedEntity[]) => void
  updateEntityStatus: (docId: string, entityId: string, status: EntityStatus) => void
  acceptEntity: (docId: string, entityId: string) => void
  rejectEntity: (docId: string, entityId: string) => void
  acceptAllEntities: (docId: string) => void
  rejectAllEntities: (docId: string) => void

  // Entity Type Visibility
  toggleEntityTypeVisibility: (type: string) => void
  setAllEntityTypesVisible: (visible: boolean) => void

  // Computed getters
  getCurrentDocument: () => SmartDocument | null
  getPendingEntities: (docId: string) => DetectedEntity[]
  getAcceptedEntities: (docId: string) => DetectedEntity[]
}

export const useDocumentStore = create<DocumentStore>()(
  persist(
    (set, get) => ({
      // Initial state
      documents: [],
      currentDocumentId: null,
      detectionState: { status: 'idle' },
      visibleEntityTypes: new Set(ALL_ENTITY_TYPES),

      // Create a new document
      createDocument: (title = 'Untitled Document') => {
        const doc: SmartDocument = {
          id: generateId(),
          title,
          content: null,
          plainText: '',
          detectedEntities: [],
          confirmedEntities: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        set((state) => ({
          documents: [doc, ...state.documents],
          currentDocumentId: doc.id,
        }))

        return doc
      },

      // Create a document from template with pre-filled content
      createDocumentFromTemplate: (title, content, plainText, templateId) => {
        const doc: SmartDocument = {
          id: generateId(),
          title,
          content,
          plainText,
          detectedEntities: [],
          confirmedEntities: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          templateId,
          hasUserContent: false,  // Template content only, no user edits yet
        }

        set((state) => ({
          documents: [doc, ...state.documents],
          currentDocumentId: doc.id,
        }))

        return doc
      },

      // Update document
      updateDocument: (id, updates) => {
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === id
              ? { ...doc, ...updates, updatedAt: new Date().toISOString() }
              : doc
          ),
        }))
      },

      // Delete document
      deleteDocument: (id) => {
        set((state) => ({
          documents: state.documents.filter((doc) => doc.id !== id),
          currentDocumentId:
            state.currentDocumentId === id ? null : state.currentDocumentId,
        }))
      },

      // Set current document
      setCurrentDocument: (id) => {
        set({ currentDocumentId: id })
      },

      // Update content
      updateContent: (id, content, plainText) => {
        const doc = get().documents.find((d) => d.id === id)
        const updates: Partial<SmartDocument> = { content, plainText }

        // If this is a template doc without user content, check if user actually edited
        if (doc && doc.templateId && doc.hasUserContent === false) {
          // Check if plainText meaningfully changed (user typed something)
          if (doc.plainText && plainText !== doc.plainText) {
            updates.hasUserContent = true
          }
        }

        get().updateDocument(id, updates)
      },

      // Set content (overwrites completely, used for AI generation)
      setContent: (id, content, plainText) => {
        get().updateDocument(id, { content, plainText })
      },

      // Analyze document for entities
      analyzeDocument: async (id) => {
        const doc = get().documents.find((d) => d.id === id)
        if (!doc || !doc.plainText || doc.plainText.length < 50) {
          return
        }

        // Skip if content hasn't changed since last analysis
        if (doc.lastAnalyzedAt && doc.plainText === get().detectionState.lastAnalyzedContent) {
          return
        }

        set({ detectionState: { status: 'analyzing' } })

        try {
          const result = await analyzeContent({
            sourceId: id,
            sourceType: 'document',
            content: doc.plainText,
            context: 'Smart document with entity detection',
          })

          // Convert facets to detected entities
          const detectedEntities: DetectedEntity[] = result.facets.map((facet: Facet) => ({
            id: generateEntityId(),
            type: facet.type,
            content: facet.content,
            evidence: facet.evidence,
            confidence: facet.confidence,
            suggestedOntologyType: facet.suggestedOntologyType,
            suggestedOntologyReason: facet.suggestedOntologyReason,
            status: 'pending' as EntityStatus,
          }))

          // Update document with detected entities
          get().updateDocument(id, {
            detectedEntities,
            lastAnalyzedAt: new Date().toISOString(),
          })

          set({
            detectionState: {
              status: 'complete',
              lastAnalyzedContent: doc.plainText,
              detectedCount: detectedEntities.length,
            },
          })
        } catch (error) {
          console.error('Document analysis failed:', error)
          set({
            detectionState: {
              status: 'error',
              error: error instanceof Error ? error.message : 'Analysis failed',
            },
          })
        }
      },

      // Add detected entities
      addDetectedEntities: (docId, entities) => {
        const doc = get().documents.find((d) => d.id === docId)
        if (!doc) return

        get().updateDocument(docId, {
          detectedEntities: [...doc.detectedEntities, ...entities],
        })
      },

      // Update entity status
      updateEntityStatus: (docId, entityId, status) => {
        const doc = get().documents.find((d) => d.id === docId)
        if (!doc) return

        const updatedEntities = doc.detectedEntities.map((e) =>
          e.id === entityId
            ? { ...e, status, decidedAt: new Date().toISOString() }
            : e
        )

        get().updateDocument(docId, { detectedEntities: updatedEntities })
      },

      // Accept entity
      acceptEntity: (docId, entityId) => {
        get().updateEntityStatus(docId, entityId, 'accepted')
      },

      // Reject entity
      rejectEntity: (docId, entityId) => {
        get().updateEntityStatus(docId, entityId, 'rejected')
      },

      // Accept all pending entities
      acceptAllEntities: (docId) => {
        const doc = get().documents.find((d) => d.id === docId)
        if (!doc) return

        const updatedEntities = doc.detectedEntities.map((e) =>
          e.status === 'pending'
            ? { ...e, status: 'accepted' as EntityStatus, decidedAt: new Date().toISOString() }
            : e
        )

        get().updateDocument(docId, { detectedEntities: updatedEntities })
      },

      // Reject all pending entities
      rejectAllEntities: (docId) => {
        const doc = get().documents.find((d) => d.id === docId)
        if (!doc) return

        const updatedEntities = doc.detectedEntities.map((e) =>
          e.status === 'pending'
            ? { ...e, status: 'rejected' as EntityStatus, decidedAt: new Date().toISOString() }
            : e
        )

        get().updateDocument(docId, { detectedEntities: updatedEntities })
      },

      // Toggle entity type visibility
      toggleEntityTypeVisibility: (type) => {
        set((state) => {
          const newSet = new Set(state.visibleEntityTypes)
          if (newSet.has(type)) {
            newSet.delete(type)
          } else {
            newSet.add(type)
          }
          return { visibleEntityTypes: newSet }
        })
      },

      // Set all entity types visible or hidden
      setAllEntityTypesVisible: (visible) => {
        set({
          visibleEntityTypes: visible ? new Set(ALL_ENTITY_TYPES) : new Set(),
        })
      },

      // Get current document
      getCurrentDocument: () => {
        const { documents, currentDocumentId } = get()
        return documents.find((d) => d.id === currentDocumentId) || null
      },

      // Get pending entities
      getPendingEntities: (docId) => {
        const doc = get().documents.find((d) => d.id === docId)
        return doc?.detectedEntities.filter((e) => e.status === 'pending') || []
      },

      // Get accepted entities
      getAcceptedEntities: (docId) => {
        const doc = get().documents.find((d) => d.id === docId)
        return doc?.detectedEntities.filter((e) => e.status === 'accepted') || []
      },
    }),
    {
      name: 'intellispot-smart-docs',
      partialize: (state) => ({
        documents: state.documents,
        currentDocumentId: state.currentDocumentId,
        // Convert Set to Array for JSON serialization
        visibleEntityTypes: Array.from(state.visibleEntityTypes),
      }),
      // Convert Array back to Set on rehydration
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.visibleEntityTypes)) {
          state.visibleEntityTypes = new Set(state.visibleEntityTypes as unknown as string[])
        } else if (state) {
          state.visibleEntityTypes = new Set(ALL_ENTITY_TYPES)
        }
      },
    }
  )
)

// Selector hooks
export const useCurrentDocument = () =>
  useDocumentStore((state) => state.getCurrentDocument())

export const useDetectionState = () =>
  useDocumentStore((state) => state.detectionState)
